// Assorted presentational helpers: avatars, prose, time picker.

import { Fragment, useState, type ReactNode } from 'react';
import type { Person } from '../domain/types.ts';
import { MINUTE, formatTime } from '../domain/time.ts';

export function Avatar({ person, size = 36, ring }: { person?: Person; size?: number; ring?: boolean }) {
  const initial = person?.name?.[0]?.toUpperCase() ?? '?';
  return (
    <span
      className={`avatar${ring ? ' avatar-ring' : ''}`}
      style={{ width: size, height: size, fontSize: size * 0.44, background: person?.color ?? 'var(--muted)' }}
      aria-label={person?.name}
      title={person?.name}
    >
      {initial}
    </span>
  );
}

/** Renders the tiny markdown subset documented in content/types.ts. */
export function Prose({ text }: { text: string }) {
  const blocks = text.trim().split(/\n{2,}/);
  return (
    <div className="prose">
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        if (lines.every((l) => /^\s*[-*] /.test(l)))
          return (
            <ul key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-*] /, ''))}</li>
              ))}
            </ul>
          );
        if (lines.every((l) => /^\s*\d+\. /.test(l)))
          return (
            <ol key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*\d+\. /, ''))}</li>
              ))}
            </ol>
          );
        // Mixed paragraph + list: render lines individually.
        if (lines.some((l) => /^\s*([-*] |\d+\. )/.test(l))) {
          const out: ReactNode[] = [];
          let list: string[] = [];
          const flush = () => {
            if (list.length) out.push(<ul key={`l${out.length}`}>{list.map((l, j) => <li key={j}>{inline(l)}</li>)}</ul>);
            list = [];
          };
          lines.forEach((l) => {
            if (/^\s*([-*] |\d+\. )/.test(l)) list.push(l.replace(/^\s*([-*] |\d+\. )/, ''));
            else {
              flush();
              out.push(<p key={`p${out.length}`}>{inline(l)}</p>);
            }
          });
          flush();
          return <Fragment key={i}>{out}</Fragment>;
        }
        if (/^#{1,4} /.test(block) && lines.length === 1) return <h4 key={i} className="prose-h">{inline(block.replace(/^#{1,4} /, ''))}</h4>;
        return <p key={i}>{inline(block.replace(/^#{1,4} /gm, '').replace(/\n/g, ' '))}</p>;
      })}
    </div>
  );
}

/** **bold** and [links](https://…) — so Ask's answers can cite their sources. */
function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const link = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/.exec(p);
    if (link)
      return (
        <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="link">
          {link[1]}
        </a>
      );
    return <Fragment key={i}>{p}</Fragment>;
  });
}

function toLocalInput(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "When did it happen?" — quick offsets plus an exact picker. */
export function WhenPicker({ value, onChange, label = 'When', quick = true }: { value: number; onChange: (t: number) => void; label?: string; /** "Now / 5 min ago…" — for something new, not when editing an old entry. */ quick?: boolean }) {
  const [base] = useState(() => Date.now());
  const offsets = [0, 5, 15, 30, 60];
  const offset = Math.round((base - value) / MINUTE);
  return (
    <div className="when">
      <span className="field-label">{label}</span>
      {quick && (
      <div className="chips">
        {offsets.map((m) => (
          <button key={m} type="button" className={`chip${offset === m ? ' on' : ''}`} onClick={() => onChange(base - m * MINUTE)}>
            {m === 0 ? 'Now' : m < 60 ? `${m} min ago` : '1 hr ago'}
          </button>
        ))}
      </div>
      )}
      <input
        type="datetime-local"
        className="input"
        value={toLocalInput(value)}
        max={toLocalInput(base + 5 * MINUTE)}
        onChange={(e) => {
          const t = new Date(e.target.value).getTime();
          if (!Number.isNaN(t)) onChange(t);
        }}
        aria-label={`${label} (exact time)`}
      />
      <span className="field-hint">{formatTime(value)}</span>
    </div>
  );
}

export function toLocalDateTimeInput(t: number) {
  return toLocalInput(t);
}

/**
 * When something finished — "woke at 17:00": quick picks (Now, 30 min, 1 hr…),
 * or type the time and nudge it ±5 minutes. A time before the start means the
 * next day (an overnight sleep). With `ongoing`, "Still going" is offered too.
 */
export function EndPicker({
  start,
  value,
  onChange,
  label = 'Finished',
  ongoing,
}: {
  start: number;
  value: number | undefined;
  onChange: (end: number | undefined) => void;
  label?: string;
  /** Label for "not finished yet" ("Still asleep"); leave out to not offer it. */
  ongoing?: string;
}) {
  const [now] = useState(() => Date.now());
  const clamp = (t: number) => Math.max(start + MINUTE, Math.min(t, Math.max(now, start + MINUTE)));
  const set = (t: number) => onChange(clamp(t));
  const durations = [20, 30, 45, 60, 90, 120];
  const lasting = value !== undefined ? Math.round((value - start) / MINUTE) : undefined;
  return (
    <div className="when end-picker">
      <span className="field-label">{label}</span>
      <div className="chips">
        {ongoing && (
          <button type="button" className={`chip${value === undefined ? ' on' : ''}`} onClick={() => onChange(undefined)}>
            {ongoing}
          </button>
        )}
        <button type="button" className={`chip${value !== undefined && Math.abs(value - now) < MINUTE ? ' on' : ''}`} onClick={() => set(now)}>
          Now
        </button>
        {durations
          .filter((m) => start + m * MINUTE <= now + MINUTE)
          .map((m) => (
            <button key={m} type="button" className={`chip${lasting === m ? ' on' : ''}`} onClick={() => set(start + m * MINUTE)}>
              {m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)}½ hr` : `${m / 60} hr`}
            </button>
          ))}
      </div>
      <div className="end-picker-row">
        <button type="button" className="chip" aria-label={`${label} 5 minutes earlier`} disabled={value === undefined} onClick={() => value !== undefined && set(value - 5 * MINUTE)}>
          −5
        </button>
        <input
          type="time"
          className="input"
          aria-label={`${label} at (time)`}
          value={value !== undefined ? formatTime(value) : ''}
          onChange={(e) => {
            if (!e.target.value) return;
            const [h, m] = e.target.value.split(':').map(Number);
            const d = new Date(start);
            let t = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime();
            if (t <= start) t += 24 * 60 * MINUTE; // e.g. went down 22:00, woke 06:15
            set(t);
          }}
        />
        <button type="button" className="chip" aria-label={`${label} 5 minutes later`} disabled={value === undefined || value + 5 * MINUTE > now} onClick={() => value !== undefined && set(value + 5 * MINUTE)}>
          +5
        </button>
        <span className="field-hint">{lasting !== undefined ? `${label === 'Woke up' ? 'slept' : 'lasted'} ${lasting >= 60 ? `${Math.floor(lasting / 60)}h ${lasting % 60}m` : `${lasting} min`}` : ongoing ? ongoing.toLowerCase() : ''}</span>
      </div>
    </div>
  );
}
